import { MinimalStepperProps } from '@components';
import { showToast } from '@design-system';
import { ApiFile } from '@hooks';
import { Audit, AuditItem, GetUsersResponse, useCreateSecurityAuditMutation, useGetUsersMutation, useSearchUsersMutation } from '@services';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const useSecurityAuditPageVM = () => {
    const [audits, setAudits] = useState<Audit[]>([
        {
            step: 'Visite préventive',
            items: [],
            files: []
        },
        {
            step: 'Affichage règlementaire',
            description: 'Les documents prévention sont à disposition sur chantier.',
            items: [
                { id: 1, label: 'PPSPS', compliant: null, comment: null },
                { id: 2, label: 'Panneau de chantier', compliant: null, comment: null },
                { id: 3, label: "Consignes en cas d'accident", compliant: null, comment: null },
                { id: 4, label: "Règles d'Or", compliant: null, comment: null },
                {
                    id: 5,
                    label: 'Le chantier, les consignes et les interventions sont présentés aux compagnons (accueil sécurité).',
                    compliant: null,
                    comment: null
                }
            ],
            files: []
        },
        {
            step: 'Hygiène',
            items: [
                { id: 1, label: 'Sanitaires', compliant: null, comment: null },
                { id: 2, label: 'Lavabos', compliant: null, comment: null },
                { id: 3, label: 'Vestiaires', compliant: null, comment: null },
                { id: 4, label: 'Réfectoire', compliant: null, comment: null },
                { id: 5, label: 'Trousse secours', compliant: null, comment: null }
            ],
            files: []
        },
        {
            step: 'Protections individuelles',
            items: [
                { id: 1, label: 'Port du casque avec jugulaire', compliant: null, comment: null },
                { id: 2, label: 'Le port des chaussures de sécurité hautes', compliant: null, comment: null },
                {
                    id: 3,
                    label: 'Port des lunettes de sécurité adapté',
                    compliant: null,
                    comment: null
                },
                {
                    id: 4,
                    label: 'Port des casque anti-bruit (obligatoire pour marteau piqueur, perforateur béton, cloueur, rainureuse, etc.)',
                    compliant: null,
                    comment: null
                },
                {
                    id: 5,
                    label: "Le casque anti-bruit (lui est obligatoire lors de l'utilisation du marteau piqueur)",
                    compliant: null,
                    comment: null
                },
                { id: 6, label: 'Port du pantalon et vêtement de travail adapté', compliant: null, comment: null }
            ],
            files: []
        },
        {
            step: 'Circulation sur le chantier',
            items: [
                {
                    id: 1,
                    label: "Circulations dégagées et éclairées à l'intérieur et à l'extérieur des bâtiments",
                    compliant: null,
                    comment: null
                },
                {
                    id: 2,
                    label: 'Évaluation des risques liés à la circulation des travailleurs et des véhicules sur le chantier',
                    compliant: null,
                    comment: null
                }
            ],
            files: []
        },
        {
            step: 'Travaux en hauteur',
            items: [
                { id: 1, label: 'Travaux en hauteur avec équipement adapté (PIRL, échafaudage,…)', compliant: null, comment: null },
                {
                    id: 2,
                    label: "Les protections collectives sont présentes, adaptées et utilisées (garde-corps en périphérie d'une trémie, aux abord d'une toiture)",
                    compliant: null,
                    comment: null
                },
                {
                    id: 3,
                    label: "Les moyens d'accès au plancher sont sécurisés et conformes aux attendus en matière de sécurité (escaliers définitifs, escaliers provisoires avec main courantes, escaliers provisoires fixés en pied et attachés en haut)",
                    compliant: null,
                    comment: null
                }
            ],
            files: []
        },
        {
            step: 'Installation électrique provisoire',
            items: [
                {
                    id: 1,
                    label: "L'installation électrique générale du chantier est vérifiée et les consignations des réseaux existants sont faites.",
                    compliant: null,
                    comment: null
                },
                {
                    id: 2,
                    label: 'Les équipements sont conformes et en bon état (coffret de chantier, installation électrique provisoire,…)',
                    compliant: null,
                    comment: null
                }
            ],
            files: []
        },
        {
            step: 'Equipement de travail',
            items: [
                {
                    id: 1,
                    label: "Les équipements de travail nécessaires à l'activité sont présents et conformes (machines fixes/ portatives, équipements, engins, véhicules…).",
                    compliant: null,
                    comment: null
                }
            ],
            files: []
        },
        {
            step: 'Récapitulatif',
            items: [],
            files: []
        }
    ]);

    const [usersOptions, setUsersOptions] = useState<{ id: string; label: string }[]>([]);

    const [currentStep, setCurrentStep] = useState<string>(audits[0].step);

    const [worksitesOptions, setWorksitesOptions] = useState<{ id: string; label: string }[]>([]);
    const [operatorsOptions, setOperatorsOptions] = useState<
        (GetUsersResponse['operators'][number] & {
            id: string;
            label: string;
        })[]
    >([]);

    const [selectedOperatorId, setSelectedOperatorId] = useState<string>('');
    const [selectedWorksiteId, setSelectedWorksiteId] = useState<string>('');
    const [usersMet, setUsersMet] = useState<Array<string>>([]);
    const [files, setFiles] = useState<ApiFile[]>([]);

    const [getOperators, { isLoading: isLoadingOperators, data: operators }] = useGetUsersMutation();

    const [searchUsers, { isLoading: isLoadingSearchUsers, data: searchUsersData }] = useSearchUsersMutation();

    const [
        createSecurityAudit,
        {
            isLoading: isLoadingCreateSecurityAudit,
            isSuccess: isSuccessCreateSecurityAudit,
            isError: isErrorCreateSecurityAudit,
            error: errorCreateSecurityAudit
        }
    ] = useCreateSecurityAuditMutation();

    const navigate = useNavigate();

    const handleSetUsersMet = (options: string[]) => {
        setUsersMet(options);
    };

    const handleNextStep = () => {
        if (!currentStep) return;

        const currentStepIndex = audits.findIndex((item) => item.step === currentStep);
        if (currentStepIndex === audits.length - 1) return;

        setCurrentStep(audits[currentStepIndex + 1].step);
        const scrollContainer = document.querySelector('.rendered-page');
        if (scrollContainer) {
            scrollContainer.scrollTop = 0;
        }
    };

    const handlePrevStep = () => {
        if (!currentStep) return;

        const currentStepIndex = audits.findIndex((item) => item.step === currentStep);
        if (currentStepIndex === 0) return;

        const scrollContainer = document.querySelector('.rendered-page');
        if (scrollContainer) {
            scrollContainer.scrollTop = 0;
        }
    };

    const navigateToAudit = (step: string) => {
        setCurrentStep(step);
    };

    const canGoBack = useMemo(() => {
        const currentStepIndex = audits.findIndex((item) => item.step === currentStep);
        return currentStepIndex > 0;
    }, [audits, currentStep]);

    const isStepCompleted = useMemo(() => {
        const currentAudit = audits.find((item) => item.step === currentStep);
        if (!currentAudit) return false;

        const currentAuditIndex = audits.findIndex((item) => item.step === currentStep);

        if (currentAuditIndex === 0) {
            return usersMet.length > 0 && selectedOperatorId && selectedWorksiteId;
        }

        const isStepCompliant = currentAudit.items.every((item) => item.compliant !== null);

        const nonCompliantItemsHaveComments = currentAudit.items
            .filter((item) => item.compliant === 'Non conforme')
            .every((item) => item.comment && item.comment.trim().length > 0);

        return isStepCompliant && nonCompliantItemsHaveComments;
    }, [audits, currentStep, usersMet, selectedOperatorId, selectedWorksiteId]);

    const hasStepItemUncompliant = useMemo(() => {
        const currentAudit = audits.find((item) => item.step === currentStep);
        if (!currentAudit) return false;

        return currentAudit.items.some((item) => item.compliant === 'Non conforme');
    }, [audits, currentStep]);

    const handleAuditItemChange = (step: string, itemId: number, compliant: AuditItem['compliant']) => {
        setAudits(
            audits.map((item) =>
                item.step === step
                    ? { ...item, items: item.items.map((item) => (item.id === itemId ? { ...item, compliant } : item)) }
                    : item
            )
        );
    };

    const handleAuditItemCommentChange = (step: string, itemId: number, comment: string) => {
        setAudits(
            audits.map((item) =>
                item.step === step ? { ...item, items: item.items.map((item) => (item.id === itemId ? { ...item, comment } : item)) } : item
            )
        );
    };

    const computedStepperSteps = useMemo((): MinimalStepperProps['steps'] => {
        return audits.map((item, index) => {
            const currentStepIndex = audits.findIndex((item) => item.step === currentStep);
            const computedStatus = index < currentStepIndex ? 'completed' : index === currentStepIndex ? 'inProgress' : 'pending';
            return {
                label: item.step,
                status: computedStatus
            };
        });
    }, [audits, currentStep]);

    const onOperatorChange = (operatorId: string) => {
        setSelectedOperatorId(operatorId);
        setSelectedWorksiteId('');
    };

    const onWorksiteChange = (worksiteId: string) => {
        setSelectedWorksiteId(worksiteId);
    };

    const isLastStep = useMemo(() => {
        return currentStep === audits[audits.length - 1].step;
    }, [currentStep, audits]);

    const mapAuditToAuditPayload = useCallback(
        () => ({
            operator_id: selectedOperatorId,
            worksite_id: selectedWorksiteId,
            request_users: usersMet,
            audits: audits
                .filter(({ step }) => step !== 'Visite préventive' && step !== 'Récapitulatif')
                .map(({ step, items, files }) => ({
                    label: step,
                    items: items.map(({ label, compliant, comment }) => ({
                        label,
                        compliant,
                        ...(comment ? { comment } : {})
                    })),
                    ...(files?.length ? { files: files.map(({ id }) => id) } : {})
                }))
        }),
        [selectedOperatorId, selectedWorksiteId, usersMet, audits]
    );

    const handleSendAudit = () => {
        createSecurityAudit(mapAuditToAuditPayload());
    };

    useEffect(() => {
        getOperators();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setFiles(audits.find((item) => item.step === currentStep)?.files ?? []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep]);

    useEffect(() => {
        if (isLoadingOperators) return;

        if (operators) {
            setOperatorsOptions(
                operators.operators.map((operator) => ({
                    ...operator,
                    id: operator.id_operator,
                    label: operator.name
                }))
            );
        }
    }, [operators, isLoadingOperators]);

    useEffect(() => {
        if (operators?.operators && selectedOperatorId) {
            const selectedOperator = operators.operators.find((op) => op.id_operator === selectedOperatorId);

            if (selectedOperator) {
                const worksites = selectedOperator.worksites.map((worksite) => ({
                    id: worksite.id,
                    label: worksite.name
                }));
                setWorksitesOptions(worksites);
            }
        }
    }, [operators, selectedOperatorId]);

    useEffect(() => {
        if (selectedWorksiteId) {
            setUsersOptions([]);
            setUsersMet([]);
            searchUsers({ key: 'worksite_id', value: selectedWorksiteId });
        }
    }, [selectedWorksiteId, searchUsers]);

    useEffect(() => {
        if (searchUsersData) {
            setUsersOptions(searchUsersData.map((item) => ({ id: item.id, label: `${item.first_name} ${item.last_name}` })));
        }
    }, [searchUsersData]);

    useEffect(() => {
        if (isLoadingCreateSecurityAudit) return;

        if (isSuccessCreateSecurityAudit) {
            showToast({
                type: 'success',
                message: 'Audit envoyé avec succès'
            });
            navigate('/sante-securite');
        }

        if (isErrorCreateSecurityAudit) {
            console.error(errorCreateSecurityAudit);
        }
    }, [isLoadingCreateSecurityAudit, isSuccessCreateSecurityAudit, isErrorCreateSecurityAudit, errorCreateSecurityAudit, navigate]);

    useEffect(() => {
        setAudits((prevAudits) =>
            prevAudits.map((item) =>
                item.step === currentStep ? { ...item, files, items: item.items.map((item) => (item.id === 1 ? { ...item } : item)) } : item
            )
        );
    }, [files, currentStep]);

    return {
        audits,
        computedStepperSteps,
        worksitesOptions,
        operatorsOptions,
        selectedOperatorId,
        selectedWorksiteId,
        usersOptions,
        usersMet,
        handleSetUsersMet,
        onOperatorChange,
        onWorksiteChange,
        handleAuditItemChange,
        handleAuditItemCommentChange,
        setFiles,
        currentStep,
        hasStepItemUncompliant,
        isStepCompleted,
        canGoBack,
        handleNextStep,
        handlePrevStep,
        navigateToAudit,
        isLastStep,
        handleSendAudit,
        isLoadingSearchUsers
    };
};
